import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext'; // Import useAuth

interface NotificationContextData {
    pendingCount: number;
    refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextData>({} as NotificationContextData);

export function NotificationProvider({ children }: { children: ReactNode }) {
    const [pendingCount, setPendingCount] = useState(0);
    const { isAuthenticated } = useAuth(); // Get auth state

    const refreshNotifications = async () => {
        if (!isAuthenticated) return; // Don't fetch if not logged in

        try {
            const [reqRes, notifRes] = await Promise.all([
                api.get('/api/requisicoes/pendentes'),
                api.get('/api/notificacoes/unread')
            ]);

            let count = 0;
            if (reqRes.data.status === 'success') {
                count += reqRes.data.data.filter((r: any) => r.status === 'pendente').length;
            }
            if (notifRes.data.status === 'success') {
                count += notifRes.data.count;
            }
            setPendingCount(count);

        } catch (error: any) {
            // Silence 401/403 errors to avoid console noise on logout/timeout
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                return;
            }
            console.error('Erro ao buscar notificações:', error);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            refreshNotifications();
            // Optional: Polling every 60s
            const interval = setInterval(refreshNotifications, 60000);
            return () => clearInterval(interval);
        } else {
            setPendingCount(0); // Reset on logout
        }
    }, [isAuthenticated]); // Re-run when auth changes

    return (
        <NotificationContext.Provider value={{ pendingCount, refreshNotifications }}>
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotifications() {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
}
